import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LanguageService, UILanguage } from './language.service';
import { MoneyCommaPipe } from './money-comma.pipe';
import { MobileCartService } from './mobile-cart.service';
import { CustomerTier, MobileSessionService } from './mobile-session.service';
import { MobileCategory, MobileProduct, MobileProductsService, TierPriceRange } from './mobile-products.service';

type DetailDictionary = {
  back: { en: string; nl: string; fr: string };
  contactPricing: { en: string; nl: string; fr: string };
  details: { en: string; nl: string; fr: string };
  categories: { en: string; nl: string; fr: string };
  quantity: { en: string; nl: string; fr: string };
  packIncrementHint: { en: string; nl: string; fr: string };
  unitPrice: { en: string; nl: string; fr: string };
  tax: { en: string; nl: string; fr: string };
  lineTotal: { en: string; nl: string; fr: string };
  addToCart: { en: string; nl: string; fr: string };
  viewCart: { en: string; nl: string; fr: string };
  productVideo: { en: string; nl: string; fr: string };
  loading: { en: string; nl: string; fr: string };
  notFound: { en: string; nl: string; fr: string };
  added: { en: string; nl: string; fr: string };
};

const DICTIONARY: DetailDictionary = {
  back: { en: 'Back to Products', nl: 'Terug naar Producten', fr: 'Retour aux Produits' },
  contactPricing: {
    en: 'Contact Us for Pricing',
    nl: 'Contacteer ons voor prijzen',
    fr: 'Contactez-nous pour les prix',
  },
  details: { en: 'Product Details', nl: 'Productdetails', fr: 'Details Produit' },
  categories: { en: 'Categories', nl: 'Categorieen', fr: 'Categories' },
  quantity: { en: 'Qty', nl: 'Aantal', fr: 'Qte' },
  packIncrementHint: {
    en: 'Quantities round up to full packs of',
    nl: 'Aantallen worden naar boven afgerond op volle verpakkingen van',
    fr: 'Les quantites sont arrondies au pack complet de',
  },
  unitPrice: { en: 'Unit Price', nl: 'Stukprijs', fr: 'Prix Unitaire' },
  tax: { en: 'Tax', nl: 'Belasting', fr: 'Taxe' },
  lineTotal: { en: 'Line Total', nl: 'Regeltotaal', fr: 'Total Ligne' },
  addToCart: { en: 'Add To Draft Order', nl: 'Voeg Toe Aan Conceptbestelling', fr: 'Ajouter au Brouillon' },
  viewCart: { en: 'View Cart', nl: 'Bekijk Winkelwagen', fr: 'Voir Panier' },
  productVideo: { en: 'Product Video', nl: 'Productvideo', fr: 'Video du Produit' },
  loading: { en: 'Loading product details...', nl: 'Productdetails laden...', fr: 'Chargement des details...' },
  notFound: { en: 'Product not found.', nl: 'Product niet gevonden.', fr: 'Produit introuvable.' },
  added: { en: 'Added to cart.', nl: 'Toegevoegd aan winkelwagen.', fr: 'Ajoute au panier.' },
};

@Component({
  selector: 'app-mobile-product-details',
  standalone: true,
  imports: [CommonModule, MoneyCommaPipe],
  templateUrl: './mobile-product-details.component.html',
  styleUrl: './mobile-product-details.component.css',
})
export class MobileProductDetailsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly languageService = inject(LanguageService);
  private readonly sessionService = inject(MobileSessionService);
  private readonly productsService = inject(MobileProductsService);
  private readonly cartService = inject(MobileCartService);
  private addedTimer: ReturnType<typeof setTimeout> | null = null;

  public readonly language = this.languageService.language;
  public readonly loading = this.productsService.loading;
  public readonly products = this.productsService.products;
  public readonly phoneHref = '+32470845847';
  public readonly selectedQuantity = signal<number>(1);
  public readonly inferredTier = signal<CustomerTier | null>(null);
  public readonly addedNotice = signal<boolean>(false);
  public readonly currentImageIndex = signal<number>(0);

  private readonly productID = signal<string>('');

  public readonly tier = computed<CustomerTier>(() => this.inferredTier() ?? 'bronze');
  public readonly product = computed<MobileProduct | undefined>(() =>
    this.products().find((item) => item.id === this.productID()),
  );

  public readonly imageURLs = computed<string[]>(() => {
    const product = this.product();
    if (!product) {
      return [];
    }
    return product.imageLinks.filter((item) => String(item || '').trim() !== '');
  });

  constructor() {
    if (!this.sessionService.isLoggedIn()) {
      void this.router.navigateByUrl('/mobile/login');
      return;
    }

    this.productID.set(this.route.snapshot.paramMap.get('id') || '');
    void this.syncCustomerTier();
    void this.productsService.load();

    this.destroyRef.onDestroy(() => {
      if (this.addedTimer) {
        clearTimeout(this.addedTimer);
      }
    });
  }

  public t(key: keyof DetailDictionary): string {
    return DICTIONARY[key][this.language()] || DICTIONARY[key].en;
  }

  public goBack(): void {
    void this.router.navigateByUrl('/mobile/dashboard');
  }

  public goToCart(): void {
    void this.router.navigateByUrl('/mobile/cart');
  }

  public setQuantity(rawValue: string): void {
    const product = this.product();
    const packageSize = product ? product.packageSize : 1;
    this.selectedQuantity.set(this.productsService.normalizeQuantity(Number(rawValue), packageSize));
  }

  public addToCart(): void {
    const product = this.product();
    if (!product) {
      return;
    }

    const addQuantity = this.normalizedSelectedQuantity();
    const existingLine = this.cartService.cartItems().find((item) => item.productID === product.id);
    const mergedQuantity = (existingLine?.quantity ?? 0) + addQuantity;
    const unitPriceCents = this.unitPriceCents(product, mergedQuantity);

    this.cartService.upsertItem({
      productID: product.id,
      productName: this.productName(product),
      quantity: mergedQuantity,
      packageSize: product.packageSize,
      unitPriceCents,
      taxPercentage: Number(product.taxPercentage || 0),
    });

    this.addedNotice.set(true);
    if (this.addedTimer) {
      clearTimeout(this.addedTimer);
    }
    this.addedTimer = setTimeout(() => {
      this.addedNotice.set(false);
      this.addedTimer = null;
    }, 1200);
  }

  public productName(product: MobileProduct): string {
    return this.pickLocalizedValue(product.nameEnglish, product.nameDutch, product.nameFrench);
  }

  public productDescription(product: MobileProduct): string {
    return this.pickLocalizedValue(
      product.descriptionEnglish,
      product.descriptionDutch,
      product.descriptionFrench,
    );
  }

  public categoryName(category: MobileCategory): string {
    return this.pickLocalizedValue(category.nameEnglish, category.nameDutch, category.nameFrench);
  }

  public unitPrice(): number {
    const product = this.product();
    if (!product) {
      return 0;
    }
    return this.centsToEuro(this.unitPriceCents(product, this.normalizedSelectedQuantity()));
  }

  public taxAmount(): number {
    const product = this.product();
    if (!product) {
      return 0;
    }
    const quantity = this.normalizedSelectedQuantity();
    const subTotal = this.unitPriceCents(product, quantity) * quantity;
    return this.centsToEuro(Math.round((subTotal * Number(product.taxPercentage || 0)) / 100));
  }

  public lineTotal(): number {
    const product = this.product();
    if (!product) {
      return 0;
    }
    const quantity = this.normalizedSelectedQuantity();
    const subTotal = this.unitPriceCents(product, quantity) * quantity;
    return this.centsToEuro(subTotal);
  }

  public packageSize(): number {
    const product = this.product();
    return this.productsService.normalizePackageSize(product?.packageSize ?? 1);
  }

  public normalizedSelectedQuantity(): number {
    return this.productsService.normalizeQuantity(this.selectedQuantity(), this.packageSize());
  }

  public activeImageURL(): string {
    const images = this.imageURLs();
    if (!images.length) {
      return '';
    }
    return images[this.safeImageIndex(this.currentImageIndex(), images.length)];
  }

  public hasMultipleImages(): boolean {
    return this.imageURLs().length > 1;
  }

  public selectImage(index: number): void {
    const images = this.imageURLs();
    if (!images.length) {
      this.currentImageIndex.set(0);
      return;
    }
    this.currentImageIndex.set(this.safeImageIndex(index, images.length));
  }

  public showPreviousImage(): void {
    const images = this.imageURLs();
    const count = images.length;
    if (count <= 1) {
      return;
    }
    const current = this.safeImageIndex(this.currentImageIndex(), count);
    this.currentImageIndex.set((current - 1 + count) % count);
  }

  public showNextImage(): void {
    const images = this.imageURLs();
    const count = images.length;
    if (count <= 1) {
      return;
    }
    const current = this.safeImageIndex(this.currentImageIndex(), count);
    this.currentImageIndex.set((current + 1) % count);
  }

  private unitPriceCents(product: MobileProduct, quantity: number): number {
    const targetQty = this.productsService.normalizeQuantity(Number(quantity), product.packageSize);
    const matchingRange = product.priceRanges.find((range) => {
      const start = Math.min(range.start, range.end);
      const end = Math.max(range.start, range.end);
      return targetQty >= start && targetQty <= end;
    });

    if (!matchingRange) {
      if (!product.priceRanges.length) {
        return 0;
      }
      return this.productsService.tierPriceCents(product.priceRanges[0], this.tier());
    }

    return this.productsService.tierPriceCents(matchingRange, this.tier());
  }

  private centsToEuro(cents: number): number {
    return Number(this.productsService.centsToEuro(cents));
  }

  private pickLocalizedValue(english: string, dutch: string, french: string): string {
    const value = this.pickByLanguage(this.language(), english, dutch, french).trim();
    if (value !== '') {
      return value;
    }

    return english || dutch || french || '-';
  }

  private pickByLanguage(language: UILanguage, english: string, dutch: string, french: string): string {
    if (language === 'nl') {
      return dutch || english || french;
    }
    if (language === 'fr') {
      return french || english || dutch;
    }
    return english || dutch || french;
  }

  private safeImageIndex(index: number, total: number): number {
    if (total <= 0) {
      return 0;
    }
    if (index < 0) {
      return 0;
    }
    if (index >= total) {
      return total - 1;
    }
    return index;
  }

  private async syncCustomerTier(): Promise<void> {
    const tier = await this.sessionService.loadCustomerTier(true);
    this.inferredTier.set(tier);
  }
}
